import axios from 'axios';
import {
  getToken,
  setToken,
  clearAllTokens,
  clearUserAndToken,
  isTokenExpired,
  isValidToken,
  getTenantId
} from './utils/token.js';
import { resolveDataTenantId } from './utils/platformViewTenant.js';
import { deepRepairUtf8Mojibake } from './utils/utf8MojibakeRepair.js';

/** 默认超时：冷启动/数据库慢时 15s 易触发 ECONNABORTED；登录接口单独再加长 */
const instance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE || '/api',
  timeout: Number(import.meta.env.VITE_AXIOS_TIMEOUT_MS) > 0
    ? Number(import.meta.env.VITE_AXIOS_TIMEOUT_MS)
    : 45000,
  withCredentials: false,
  paramsSerializer: {
    indexes: null // 禁用数组索引，避免categoryIds[]=1的格式
  }
});

instance.interceptors.request.use(config => {
  // 获取访问令牌
  const token = getToken();

  // 如果有有效令牌，设置 Authorization 头
  if (isValidToken(token)) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  // 租户：GET params 自动补全；POST body 中 'default' 替换为 JWT 租户
  const jwtTenant = resolveDataTenantId();
  const params = config.params;
  if (params && typeof params === 'object') {
    const tid = params.tenantId;
    if (tid == null || tid === '' || tid === 'default' || tid === 'default-tenant') {
      config.params = { ...params, tenantId: jwtTenant };
    }
  }
  const data = config.data;
  if (data && typeof data === 'object' && !(data instanceof FormData)) {
    const tid = data.tenantId;
    if (tid == null || tid === '' || tid === 'default' || tid === 'default-tenant') {
      config.data = { ...data, tenantId: jwtTenant };
    }
  }

  return config;
});

instance.interceptors.response.use(
  res => {
    // 数据归一化处理：允许把 []、{ list:[] }、{ data:[] } 归一到 { list:[], total }
    const payload = res.data;
    if (Array.isArray(payload)) {
      res.data = { list: payload, total: payload.length };
    } else if (payload?.data && Array.isArray(payload.data)) {
      // 对于有 success 字段的响应，保持原有结构，只添加 list 字段
      if (payload.success !== undefined) {
        res.data = { ...payload, list: payload.data, total: payload.total ?? payload.data.length };
      } else {
        res.data = { list: payload.data, total: payload.total ?? payload.data.length };
      }
    } else if (payload?.list) {
      res.data = payload;
    }

    if (res.data != null && typeof res.data === 'object'
        && !(res.data instanceof Blob)
        && !(res.data instanceof ArrayBuffer)
        && !(typeof FormData !== 'undefined' && res.data instanceof FormData)) {
      res.data = deepRepairUtf8Mojibake(res.data);
    }

    return res;
  },
  async err => {
    const originalRequest = err.config;
    const status = err.response?.status;
    const responseData = err.response?.data;

    if (status === 403 && responseData?.needsTenantJoin === true) {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/onboarding')) {
        window.location.assign('/onboarding/tenant');
      }
      return Promise.reject(err);
    }

    // 未认证常见为 401；部分环境下无 JWT 会返回 403 Access Denied，Token 已失效时同样回登录
    const authExpired =
      status === 401 ||
      (status === 403 && (!getToken() || isTokenExpired() || !isValidToken(getToken())));

    if (authExpired) {
      const currentUrl = originalRequest?.url || '';
      const isLoginRequest =
        currentUrl.includes('/users/login') ||
        currentUrl.includes('/users/register') ||
        currentUrl.includes('/login/sms') ||
        currentUrl.includes('/login/phone') ||
        currentUrl.endsWith('/login');

      if (!isLoginRequest) {
        clearUserAndToken();
        window.dispatchEvent(new CustomEvent('app:need-login'));
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.assign('/login?reason=relogin');
        }
      }
      return Promise.reject(err);
    }

    return Promise.reject(err);
  }
);

/**
 * 同一 GET（URL + 参数）在飞行期内只发一次，结果回放给所有调用方。
 * 仅合并"真正并发"的相同请求，不做结果缓存，因此不改变数据新鲜度语义。
 * 带 signal / cancelToken / responseType 的请求不参与，避免一方 abort 影响另一方。
 */
const inflightGetRequests = new Map();

function inflightGetKey(url, params) {
  if (!params || typeof params !== 'object') {
    return url;
  }
  try {
    const parts = Object.keys(params)
      .filter((k) => k !== '_t') // 破坏缓存的时间戳不参与去重
      .sort()
      .map((k) => `${k}=${JSON.stringify(params[k] ?? null)}`);
    return `${url}?${parts.join('&')}`;
  } catch (e) {
    return null;
  }
}

const rawGet = instance.get.bind(instance);
instance.get = (url, config) => {
  if (config?.signal || config?.cancelToken || config?.responseType) {
    return rawGet(url, config);
  }
  const key = inflightGetKey(url, config?.params);
  if (!key) {
    return rawGet(url, config);
  }
  const pending = inflightGetRequests.get(key);
  if (pending) {
    return pending;
  }
  const request = rawGet(url, config).finally(() => {
    inflightGetRequests.delete(key);
  });
  inflightGetRequests.set(key, request);
  return request;
};

export default instance;
export const api = instance;

// 用户管理API
export function getUsers() {
  return instance.get('/users');
}

export function getUsersPage(params) {
  return instance.get('/users/page', { params });
}

export function getActiveUsers() {
  return instance.get('/users/active');
}

export function searchUsers(keyword) {
  return instance.get('/users/search', { params: { keyword } });
}

export function getUsersByOrganization(organizationId) {
  return instance.get(`/users/organization/${organizationId}`);
}

export function createUser(data) {
  return instance.post('/users', data);
}

export function updateUser(id, data) {
  return instance.put(`/users/${id}`, data);
}

export function deleteUser(id) {
  return instance.delete(`/users/${id}`);
}

export function updateUserStatus(id, status) {
  return instance.put(`/users/${id}/status`, { status });
}

export function existsByUsername(username) {
  return instance.get('/users/exists', { params: { username } });
}

export function existsByEmail(email) {
  return instance.get('/users/exists/email', { params: { email } });
}

export function login(data) {
  const loginTimeout =
    Number(import.meta.env.VITE_LOGIN_TIMEOUT_MS) > 0
      ? Number(import.meta.env.VITE_LOGIN_TIMEOUT_MS)
      : 120000;
  return instance.post('/users/login', data, { timeout: loginTimeout });
}

export function logout() {
  return instance.post('/users/logout');
}

export function register(data) {
  return instance.post('/users/register', data);
}

export function sendResetCode(data) {
  return instance.post('/users/send-reset-code', data);
}

export function resetPassword(data) {
  return instance.post('/users/reset-password', data);
}

export function getUserRoles(userId) {
  return instance.get(`/users/${userId}/roles`);
}

export function assignRolesToUser(userId, roleIds) {
  return instance.post(`/users/${userId}/roles`, { roleIds });
}

export function getUserPermissions(userId) {
  return instance.get(`/users/${userId}/permissions`);
}

export function getUsersByRole(roleId) {
  return instance.get(`/users/role/${roleId}`);
}

// 角色管理API
export function getRoles(params) {
  return instance.get('/roles', { params });
}

export function getRolesPage(params) {
  return instance.get('/roles/page', { params });
}

export function getActiveRoles() {
  return instance.get('/roles/active');
}

export function searchRoles(keyword) {
  return instance.get('/roles/search', { params: { keyword } });
}

export function getRoleById(id) {
  return instance.get(`/roles/${id}`);
}

export function createRole(data) {
  return instance.post('/roles', data);
}

export function updateRole(id, data) {
  return instance.put(`/roles/${id}`, data);
}

export function deleteRole(id) {
  return instance.delete(`/roles/${id}`);
}

export function updateRoleStatus(id, status) {
  return instance.put(`/roles/${id}/status`, { status });
}

export function getRolePermissions(roleId) {
  return instance.get(`/roles/${roleId}/permissions`);
}

export function assignPermissionsToRole(roleId, permissionIds) {
  return instance.post(`/roles/${roleId}/permissions`, { permissionIds });
}

export function existsByRoleCode(roleCode) {
  return instance.get('/roles/exists/code', { params: { roleCode } });
}

export function existsByRoleName(name) {
  return instance.get('/roles/exists/name', { params: { name } });
}

export function getRolesWithUsers() {
  return instance.get('/roles/with-users');
}

// 权限管理API
export function getPermissions() {
  return instance.get('/permissions');
}

export function getPermissionsPage(params) {
  return instance.get('/permissions/page', { params });
}

export function getActivePermissions() {
  return instance.get('/permissions/active');
}

export function getPermissionsByModule(module) {
  return instance.get(`/permissions/module/${module}`);
}

export function getPermissionsByType(permissionType) {
  return instance.get(`/permissions/type/${permissionType}`);
}

export function searchPermissions(keyword) {
  return instance.get('/permissions/search', { params: { keyword } });
}

export function getAllModules() {
  return instance.get('/permissions/modules');
}

export function getAllActions() {
  return instance.get('/permissions/actions');
}

export function getPermissionById(id) {
  return instance.get(`/permissions/${id}`);
}

export function createPermission(data) {
  return instance.post('/permissions', data);
}

export function updatePermission(id, data) {
  return instance.put(`/permissions/${id}`, data);
}

export function deletePermission(id) {
  return instance.delete(`/permissions/${id}`);
}

export function updatePermissionStatus(id, status) {
  return instance.put(`/permissions/${id}/status`, { status });
}

export function existsByPermissionCode(permissionCode) {
  return instance.get('/permissions/exists/code', { params: { permissionCode } });
}

// 组织管理API
export function getOrganizations() {
  return instance.get('/organizations');
}

export function getOrganizationsPage(params) {
  return instance.get('/organizations/page', { params });
}

export function getActiveOrganizations() {
  return instance.get('/organizations/active');
}

export function searchOrganizations(keyword) {
  return instance.get('/organizations/search', { params: { keyword } });
}

export function getOrganizationsByCategory(category) {
  return instance.get(`/organizations/category/${category}`);
}

export function getOrganizationById(id) {
  return instance.get(`/organizations/${id}`);
}

export function createOrganization(data) {
  return instance.post('/organizations', data);
}

export function updateOrganization(id, data) {
  return instance.put(`/organizations/${id}`, data);
}

export function getDefaultTenantId() {
  return instance.get('/organizations/default-tenant-id');
}

export function deleteOrganization(id) {
  return instance.delete(`/organizations/${id}`);
}

export function updateOrganizationStatus(id, status) {
  return instance.put(`/organizations/${id}/status`, { status });
}

export function existsByOrganizationCode(organizationCode) {
  return instance.get('/organizations/exists/code', { params: { organizationCode } });
}

export function getAllCategories() {
  return instance.get('/organizations/categories');
}

// 租户管理API
export function getTenants() {
  return instance.get('/tenants');
}

export function createTenant(data) {
  return instance.post('/tenants', data);
}

export function updateTenant(id, data) {
  return instance.put(`/tenants/${id}`, data);
}

export function deleteTenant(id) {
  return instance.delete(`/tenants/${id}`);
}

// 操作日志API
export function getOperationLogs(query = '') {
  return instance.get(`/operation-logs${query ? '?' + query : ''}`);
}

export function searchOperationLogs(params) {
  return instance.get('/operation-logs/search', { params });
}

export function getRecentLogs() {
  return instance.get('/operation-logs/recent');
}

export function getOperationTypeStats() {
  return instance.get('/operation-logs/stats/operation-type');
}

export function getModuleStats() {
  return instance.get('/operation-logs/stats/module');
}

export function exportOperationLogs(params) {
  return instance.get('/operation-logs/export', { params });
}

export function createOperationLog(data) {
  return instance.post('/operation-logs', data);
}

export function updateOperationLog(id, data) {
  return instance.put(`/operation-logs/${id}`, data);
}

export function deleteOperationLog(id) {
  return instance.delete(`/operation-logs/${id}`);
}

// 设备管理API
export function getMachines() {
  return instance.get('/machines');
}

export function createMachine(data) {
  return instance.post('/machines', data);
}

export function updateMachine(id, data) {
  return instance.put(`/machines/${id}`, data);
}

export function deleteMachine(id) {
  return instance.delete(`/machines/${id}`);
}

// 设备租赁API
export function getMachineRentals() {
  return instance.get('/machine-rentals');
}

export function createMachineRental(data) {
  return instance.post('/machine-rentals', data);
}

export function updateMachineRental(id, data) {
  return instance.put(`/machine-rentals/${id}`, data);
}

export function deleteMachineRental(id) {
  return instance.delete(`/machine-rentals/${id}`);
}

// 员工管理API
export function getEmployees(params = {}) {
  if (params.keyword) {
    return instance.get('/employees/search', { params: { keyword: params.keyword } });
  }
  return instance.get('/employees');
}

export function getEmployeesPage(params) {
  return instance.get('/employees/page', { params });
}

export function getActiveEmployees() {
  return instance.get('/employees/active');
}

export function searchEmployees(keyword) {
  return instance.get('/employees/search', { params: { keyword } });
}

export function getEmployeeById(id) {
  return instance.get(`/employees/${id}`);
}

export function createEmployee(data) {
  return instance.post('/employees', data);
}

export function updateEmployee(id, data) {
  return instance.put(`/employees/${id}`, data);
}

export function deleteEmployee(id) {
  return instance.delete(`/employees/${id}`);
}

export function batchDeleteEmployees(ids) {
  return instance.delete('/employees/batch', { data: ids });
}

export function updateEmployeeStatus(id, status) {
  return instance.put(`/employees/${id}/status`, { status });
}

export function getEmployeesByDepartment(departmentId) {
  return instance.get(`/employees/department/${departmentId}`);
}

export function getEmployeesByRole(roleId) {
  return instance.get(`/employees/role/${roleId}`);
}

export function getEmployeeStatistics() {
  return instance.get('/employees/statistics');
}

export function resetEmployeePassword(id) {
  return instance.put(`/employees/${id}/reset-password`);
}

export function importEmployees(employees) {
  return instance.post('/employees/import', employees);
}

// 农资管理API
export function getMaterials() {
  return instance.get('/materials');
}

export function searchMaterials(params) {
  return instance.get('/materials/search', { params });
}

export function createMaterial(data) {
  return instance.post('/materials', data);
}

export function updateMaterial(id, data) {
  return instance.put(`/materials/${id}`, data);
}

export function updateMaterialStatus(id, status) {
  return instance.put(`/materials/${id}/status`, { status });
}

export function deleteMaterial(id) {
  return instance.delete(`/materials/${id}`);
}

// 病虫害诊断API
/**
 * 创建图像诊断记录
 * @param {FormData} formData 包含userId, plantType, image
 */
export function createImageDiagnosis(formData) {
  return instance.post('/diagnosis/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
}

/**
 * 执行图像诊断
 * @param {string} diagnosisId 诊断记录ID
 * @param {FormData} formData 包含images文件
 */
export function performImageDiagnosis(diagnosisId, formData) {
  return instance.post(`/diagnosis/${diagnosisId}/perform-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
}

/**
 * 查询诊断结果
 * @param {string} diagnosisId 诊断记录ID
 */
export function getDiagnosisById(diagnosisId) {
  return instance.get(`/diagnosis/${diagnosisId}`);
}

/**
 * 创建文字诊断记录
 * @param {Object} data 包含userId, plantType, symptomsDescription
 */
export function createTextDiagnosis(data) {
  return instance.post('/diagnosis/text', data);
}

/**
 * 执行文字诊断
 * @param {string} diagnosisId 诊断记录ID
 */
export function performTextDiagnosis(diagnosisId) {
  return instance.post(`/diagnosis/${diagnosisId}/perform-text`);
}

// 病害诊断-分类与多症状联合诊断API


// AI智能问答API
export function aiTextDiagnosis({ plantType = '', symptomsDescription }) {
  return instance.post('/diagnosis/ai-text', { plantType, symptomsDescription });
}

// 智能植保建议API
export function getPlantingAdvice({ province, city, district, latitude, longitude, elevation }) {
  return instance.post('/diagnosis/planting-advice', {
    province,
    city,
    district,
    latitude,
    longitude,
    elevation
  });
}

// 其他API可按需添加

// 图片上传API（通用）
export async function uploadImage(file) {
  const formData = new FormData();
  formData.append('file', file);
  // 注意：后端接口为 /api/upload/image
  try {
    const res = await api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    // 兼容success字段
    if (res.data && res.data.success && res.data.url) {
      return res.data.url;
    }
    console.error('uploadImage 失败，响应格式不正确:', res.data);
    throw new Error(res.data?.message || '图片上传失败');
  } catch (error) {
    console.error('uploadImage 异常:', error);
    const status = error?.response?.status;
    const serverMsg = error?.response?.data?.message;
    if (serverMsg) throw new Error(serverMsg);
    if (status === 413) throw new Error('图片过大，服务器拒绝接收');
    if (status === 401) throw new Error('登录已过期，请重新登录');
    throw error;
  }
}

// 商品图片上传API（使用product_code作为文件夹名）
export async function uploadProductImage(file, productId, productCode, imageType = 'images') {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('productId', productId);
  formData.append('productCode', productCode);
  formData.append('imageType', imageType);

  try {
    const res = await api.post('/upload/product-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    if (res.data && res.data.success && res.data.url) {
      return res.data.url;
    }
    console.error('uploadProductImage 失败，响应格式不正确:', res.data);
    throw new Error('商品图片上传失败');
  } catch (error) {
    console.error('uploadProductImage 异常:', error);
    throw error;
  }
}

export function getDiagnosisHistory(userId) {
  return instance.get(`/diagnosis/user/${userId}`);
}

export function aiImageDiagnosis(formData) {
  return instance.post('/diagnosis/ai-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
}

// 会员系统API
export function getMembershipLevels() {
  return instance.get('/membership/levels');
}

export function getUserMembership() {
  return instance.get('/membership/user-info');
}

export function checkFeaturePermission(featureCode) {
  return instance.post('/membership/check-permission', { featureCode });
}

export function checkUsageLimit(featureType) {
  return instance.post('/membership/check-usage', { featureType });
}

export function createMembershipOrder(levelId) {
  return instance.post('/membership/create-order', { levelId });
}

export function handlePaymentSuccess(orderId, paymentMethod, transactionId) {
  return instance.post('/membership/payment-success', {
    orderId,
    paymentMethod,
    transactionId
  });
}

export function incrementUsage(featureType) {
  return instance.post('/membership/increment-usage', { featureType });
}

export function getMembershipStatistics() {
  return instance.get('/membership/statistics');
}

export function getMembershipOrders(userId) {
  return instance.get(`/membership/orders/${userId}`);
}

export function getCoupons() {
  return instance.get('/membership/coupons');
}

export function applyCoupon(orderId, couponCode) {
  return instance.post('/membership/apply-coupon', { orderId, couponCode });
}

// 会员管理API
export function getMenuMembershipConfigs() {
  return instance.get('/menu-membership/configs');
}

export function getMembershipRequiredMenus() {
  return instance.get('/menu-membership/membership-required');
}

export function createMenuMembershipConfig(config) {
  return instance.post('/menu-membership/configs', config);
}

export function updateMenuMembershipConfig(id, config) {
  return instance.put(`/menu-membership/configs/${id}`, config);
}

export function deleteMenuMembershipConfig(id) {
  return instance.delete(`/menu-membership/configs/${id}`);
}

export function resetUserMenuUsage() {
  return instance.post('/menu-membership/reset-user-usage');
}

export function getSystemMenus() {
  return instance.get('/menu-membership/system-menus');
}

export function importSystemMenus() {
  return instance.post('/menu-membership/import-system-menus');
}

export function getMenuMembershipConfig(menuCode) {
  return instance.get(`/menu-membership/configs/${menuCode}`);
}

export function checkMenuAccess(menuCode) {
  return instance.get(`/menu-membership/check-access/${menuCode}`);
}

export function useMenuTrial(menuCode) {
  return instance.post(`/menu-membership/use-trial/${menuCode}`);
}

/** 读取用户某菜单最近业务上下文（驾驶舱上次农场 ID 等） */
export function getMenuLastContext(menuCode) {
  return instance.get(`/menu-membership/last-context/${encodeURIComponent(menuCode)}`);
}

/** 写入用户某菜单最近业务上下文 */
export function saveMenuLastContext(menuCode, lastContext) {
  return instance.put('/menu-membership/last-context', { menuCode, lastContext });
}

/**
 * AI多模态对话（支持文字+图片）
 * @param {FormData} formData 包含userId, plantType, symptomsDescription, images
 */
export function aiMultimodalChat(formData) {
  return instance.post('/diagnosis/ai-multimodal', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
}

export function aiEnvironmentalAnalysis(formData) {
  return instance.post('/diagnosis/ai-environmental', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
}

// 第三方登录API
export function smsLogin(data) {
  return instance.post('/users/login/sms', data);
}



export function wechatLogin(code) {
  return instance.post('/users/wechat-login', { code });
}

export function alipayLogin(code) {
  return instance.post('/users/alipay-login', { code });
}

export function bindAccount(data) {
  return instance.post('/users/bind-account', data);
}

export function createAccount(data) {
  return instance.post('/users/create-account', data);
}

// 发送短信验证码API
export function sendSmsCode(phone, tenantId = 'default-tenant') {
  return instance.post('/users/send-sms-code', { phone, tenantId });
}

/**
 * 欢迎页聚合接口：一次性获取地理位置、天气、种植建议
 * @param {Object} params { latitude, longitude, province, city, district }
 * @returns Promise
 */
export function getWelcomeOverview(params) {
  return instance.get('/welcome/overview', { params });
}

/** 驾驶舱 / 欢迎页：按经纬度拉取实时天气（对接 GET /api/external/weather/coordinates） */
export function getExternalWeatherByCoordinates(latitude, longitude) {
  return instance.get('/external/weather/coordinates', {
    params: { latitude, longitude },
  });
}

/** 按日历日拉取真实气象（GET /api/external/weather/daily，Open-Meteo 日数据 + 当日实况） */
export function getExternalWeatherDaily(latitude, longitude, date) {
  return instance.get('/external/weather/daily', {
    params: { latitude, longitude, date },
  });
}

/** 15 日预报（GET /api/external/weather/forecast，Open-Meteo，含农事影响字段） */
export function getExternalWeatherForecast(latitude, longitude) {
  return instance.get('/external/weather/forecast', {
    params: { latitude, longitude },
  });
}

/** 指定日历日逐时气象（GET /api/external/weather/hourly，Open-Meteo hourly，供 24h 趋势图） */
export function getExternalWeatherHourly(latitude, longitude, date) {
  return instance.get('/external/weather/hourly', {
    params: { latitude, longitude, date },
  });
}

/** 智慧农场驾驶舱 · 全景：聚合农场、地块、IoT、灌溉、预警（库内数据） */
export function getSmartFarmPanorama(params) {
  return instance.get('/smart-farm-cockpit/panorama', { params });
}

export function getWeatherData(params) {
  // 排查高德地理编码 infocode 30001 等：核对浏览器发出的查询串与编码
  console.log('[气象 API] GET /weather/data 请求参数:', params, {
    locationRaw: params?.location,
    locationType: typeof params?.location,
    locationLen: params?.location != null ? String(params.location).length : 0
  });
  return instance.get('/weather/data', { params });
}

/** 服务端按客户端 IP 调高德 IP 定位；可选 params.hintIp：仅当服务端判定为回环时采纳（浏览器侧公网 IPv4） */
export function getWeatherIpLocation(params) {
  return instance.get('/weather/ip-location', { params: params || {} });
}

/** 浏览器直连测公网 IPv4（localhost 开发时作为 hintIp；多源降级避免单站不可用） */
export async function fetchPublicIpv4Hint() {
  const abortMs = (ms) => {
    const c = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const t = c ? setTimeout(() => c.abort(), ms) : null;
    return { signal: c?.signal, clear: () => t && clearTimeout(t) };
  };
  const tryPlain = async (url) => {
    const { signal, clear } = abortMs(6000);
    try {
      const r = await fetch(url, signal ? { signal } : {});
      clear();
      const t = (await r.text()).trim();
      return /^\d{1,3}(\.\d{1,3}){3}$/.test(t) ? t : null;
    } catch {
      clear();
      return null;
    }
  };
  // 不用 api.ipify.org：国内常 RST，浏览器仍会打 Failed to load resource
  return (await tryPlain('https://ipv4.icanhazip.com/'))
    || (await tryPlain('https://checkip.amazonaws.com/'))
    || null;
}

/** 逆地理编码（OpenStreetMap Nominatim 代理），用于经纬度反查省市区 */
export function reverseGeocode(lat, lon) {
  return instance.get('/location/reverse-geocode', {
    params: { lat, lon }
  });
}

export function getWeatherExtremeStats(params) {
  console.log('[气象 API] GET /weather/extreme-stats 请求参数:', params);
  return instance.get('/weather/extreme-stats', { params });
}

// 行政区划下拉（后端 area_code_address_location_map）
export function getAreaCodeProvinces() {
  return instance.get('/area-codes/provinces');
}
export function getAreaCodeCities(params) {
  return instance.get('/area-codes/cities', { params });
}
export function getAreaCodeDistricts(params) {
  return instance.get('/area-codes/districts', { params });
}

export function getWaterResourcesData(params) {
  return instance.get('/water-resources/data', { params });
}

export function getWaterResourceById(id) {
  return instance.get(`/water-resources/${id}`);
}

export function createWaterResource(data) {
  return instance.post('/water-resources', data);
}

export function updateWaterResource(id, data) {
  return instance.put(`/water-resources/${id}`, data);
}

export function deleteWaterResource(id) {
  return instance.delete(`/water-resources/${id}`);
}

export function createWaterResourceTable() {
  return instance.post('/admin/water-resources/create-table');
}

export function getSoilAnalysisData(params) {
  return instance.get('/soil-analysis/data', { params });
}

export function getSoilAnalysisById(id) {
  return instance.get(`/soil-analysis/${id}`);
}

export function createSoilAnalysis(data) {
  return instance.post('/soil-analysis', data);
}

export function updateSoilAnalysis(id, data) {
  return instance.put(`/soil-analysis/${id}`, data);
}

export function deleteSoilAnalysis(id) {
  return instance.delete(`/soil-analysis/${id}`);
}

export function batchDeleteSoilAnalysis(ids) {
  return instance.delete('/soil-analysis/batch', { data: ids });
}

/** 智能设备土壤分析仪列表 */
export function listSoilAnalysisDevices(params) {
  return instance.get('/soil-analysis/soil-devices', { params });
}

/**
 * 从土壤分析仪刷新并写入测土台账（同日幂等；forceNew=true 强制新一条）
 * body: { tenantId, farmId, landId, deviceId, forceNew }
 */
export function syncSoilAnalysisFromDevices(data) {
  return instance.post('/soil-analysis/sync-from-devices', data || {});
}

export function getPlantingAdviceData(params) {
  return instance.get('/planting-advice/data', { params });
}

/** 获取气候数据（对接 GET /api/climate，返回 List<ClimateData>） */
export function getClimateData() {
  return instance.get('/climate');
}

/** 本地气候：用户位置-站点匹配结果；可传 lat/lng 按业务坐标瞬时匹配 */
export function getLocalClimateStationMatch(params) {
  return instance.get('/climate/local/station-match', { params });
}

/** 本地气候：重新匹配最近气象站；可传 lat/lng 按业务坐标重算（不覆盖用户匹配表） */
export function refreshLocalClimateStationMatch(params) {
  return instance.post('/climate/local/station-match/refresh', null, { params });
}

/** 本地气候：手动选择候选气象站；坐标模式下可附带 lat/lng */
export function selectLocalClimateStation(stationId, extra = {}) {
  return instance.post('/climate/local/station-match/select', { stationId, ...extra });
}

/** 本地气候：当前选中气象站基础统计；可传 lat/lng/stationId */
export function getLocalClimateSummary(params) {
  return instance.get('/climate/local/summary', { params });
}

/** 获取地区列表（对接 GET /api/regions） */
export function getRegions(params) {
  return instance.get('/regions', { params });
}

/** 获取作物列表（对接 GET /api/crops） */
export function getCrops() {
  return instance.get('/crops');
}

/** 获取干预计划列表（对接 GET /api/intervention-plans，依赖阶段三融合分析引擎） */
export function getInterventionPlans(params) {
  return instance.get('/intervention-plans', { params });
}

/** 按地块获取干预计划（对接 GET /api/intervention-plans/by-field，含地块土壤/灌溉环境增强） */
export function getInterventionPlansByField(params) {
  return instance.get('/intervention-plans/by-field', { params });
}

/** 获取极端气候预警列表及统计（对接 GET /api/extreme-climate-warnings，事项五） */
export function getExtremeClimateWarnings(params) {
  return instance.get('/extreme-climate-warnings', { params });
}

/** @deprecated 请使用 getClimateData；与 getClimateData 同请求 /climate，仅保留兼容 */
export function getLocalClimateData(params) {
  return instance.get('/climate', { params });
}

export function getHistoricalData(params) {
  return instance.get('/historical-data', { params });
}

export function getDisasterWarningData(params) {
  return instance.get('/disaster-warning/data', { params });
}

export function getTrendAnalysisData(params) {
  return instance.get('/weather/trend-analysis', { params });
}

export function getTenantsData(params) {
  return instance.get('/tenants', { params });
}

// 微信支付API
export function generateWechatPayQrcode(outTradeNo, total, subject, attach, productId) {
  return instance.post('/wechat-pay/qrcode', null, {
    params: { outTradeNo, total, subject, attach, productId }
  });
}

export function getWechatPayOrderStatus(outTradeNo) {
  return instance.get(`/wechat-pay/order/${outTradeNo}/status`);
}

// 销售记录相关API - 使用商城订单数据
export function getSalesData(params) {
  return instance.get('/mall/orders', { params });
}

export function getSalesRecordById(id) {
  return instance.get(`/mall/orders/${id}`);
}

export function createSalesRecord(data) {
  return instance.post('/mall/orders', data);
}

export function updateSalesRecord(id, data) {
  return instance.put(`/mall/orders/${id}`, data);
}

export function deleteSalesRecord(id) {
  return instance.delete(`/mall/orders/${id}`);
}

export function batchDeleteSalesRecords(ids) {
  return instance.delete('/mall/orders/batch', { data: ids });
}

export function searchSalesRecords(params) {
  return instance.get('/mall/orders/search', { params });
}

export function updateOrderStatus(id, orderStatus) {
  return instance.put(`/mall/orders/${id}/status`, { orderStatus });
}

export function batchUpdateOrderStatus(ids, orderStatus) {
  return instance.put('/mall/orders/batch/status', { ids, orderStatus });
}

export function getSalesStatistics() {
  return instance.get('/mall/orders/statistics');
}

export function generateOrderNumber() {
  return instance.get('/mall/orders/generate-order-number');
}

export function getPointsRulesData(params) {
  return instance.get('/mall/points-rules', { params });
}

export function getMarketingCampaignsData(params) {
  return instance.get('/mall/marketing-campaigns', { params });
}

export function getLogisticsData(params) {
  return instance.get('/mall/logistics', { params });
}

export function getInventoryData(params) {
  return instance.get('/mall/inventory', { params });
}


export function getResourceMonitoringData(params) {
  return instance.get('/resource-monitoring/data', { params });
}

export function getMySharesData(params) {
  return instance.get('/profile/my-interactions', { params: { ...params, tab: 'shares' } });
}

export function getMyLikesData(params) {
  return instance.get('/knowledge/likes/my', { params });
}

export function getMyFavoritesData(params) {
  return instance.get('/knowledge/favorites/my', { params });
}

export function getMyCommentsData(params) {
  return instance.get('/knowledge/comments/my', { params });
}

export function getPermissionUsersData(params) {
  return instance.get('/permissions/users', { params });
}

export function getOperationLogsData(params) {
  return instance.get('/operation-logs', { params });
}

export function getFieldOperationsData(params) {
  return instance.get('/field-operations', { params });
}

export function getDataVisualizationData(params) {
  return instance.get('/data-visualization', { params });
}

// ===== 资产管理API =====

// 获取资产列表
export const getAssets = (params) => {
  return instance.get('/assets', { params });
};

// 获取资产详情
export const getAsset = (id) => {
  return instance.get(`/assets/${id}`);
};

// 创建资产
export const createAsset = (data) => {
  return instance.post('/assets', data);
};

// 更新资产
export const updateAsset = (id, data) => {
  return instance.put(`/assets/${id}`, data);
};

// 删除资产
export const deleteAsset = (id) => {
  return instance.delete(`/assets/${id}`);
};

// 批量删除资产
export const batchDeleteAssets = (ids) => {
  return instance.delete('/assets/batch', { data: ids });
};

// 更新资产状态
export const updateAssetStatus = (id, status) => {
  return instance.patch(`/assets/${id}/status`, null, { params: { status } });
};

// 获取资产统计信息
export const getAssetStatistics = () => {
  return instance.get('/assets/statistics');
};

// 获取资产分类列表
export const getAssetCategories = () => {
  return instance.get('/asset-categories');
};

// 创建资产分类
export const createAssetCategory = (data) => {
  return instance.post('/asset-categories', data);
};

// 更新资产分类
export const updateAssetCategory = (id, data) => {
  return instance.put(`/asset-categories/${id}`, data);
};

// 删除资产分类
export const deleteAssetCategory = (id) => {
  return instance.delete(`/asset-categories/${id}`);
};

// 获取资产使用记录
export const getAssetUsageRecords = (assetId, params) => {
  return instance.get(`/assets/${assetId}/usage-records`, { params });
};

// 开始使用资产
export const startAssetUsage = (data) => {
  return instance.post('/asset-usage/start', data);
};

// 结束使用资产
export const endAssetUsage = (data) => {
  return instance.post('/asset-usage/end', data);
};

// 获取资产维护记录（与后端 GET /api/assets/records/maintenance?assetId= 对齐）
export const getAssetMaintenanceRecords = (assetId, params) => {
  return instance.get('/assets/records/maintenance', {
    params: { assetId, ...(params && typeof params === 'object' ? params : {}) }
  });
};

// 创建维护记录
export const createMaintenanceRecord = (data) => {
  return instance.post('/asset-maintenance', data);
};

// 更新维护记录
export const updateMaintenanceRecord = (id, data) => {
  return instance.put(`/asset-maintenance/${id}`, data);
};

// 删除维护记录
export const deleteMaintenanceRecord = (id) => {
  return instance.delete(`/asset-maintenance/${id}`);
};

// 销售记录（独立模块）API - /api/sales-records
export const getAllSalesRecords = () => instance.get('/sales-records');
export const getSalesRecord = (id) => instance.get(`/sales-records/${id}`);
export const createSalesRecordV2 = (data) => instance.post('/sales-records', data);
export const updateSalesRecordV2 = (id, data) => instance.put(`/sales-records/${id}`, data);
export const deleteSalesRecordV2 = (id) => instance.delete(`/sales-records/${id}`);
export const batchDeleteSalesRecordsV2 = (ids) => instance.delete('/sales-records/batch', { data: ids });
export const searchSalesRecordsV2 = (params) => instance.get('/sales-records/search', { params });
export const updateOrderStatusV2 = (id, orderStatus) => instance.put(`/sales-records/${id}/status`, { orderStatus });
export const batchUpdateOrderStatusV2 = (ids, orderStatus) => instance.put('/sales-records/batch/status', { ids, orderStatus });
export const getSalesStatisticsV2 = () => instance.get('/sales-records/statistics');
export const generateOrderNumberV2 = () => instance.get('/sales-records/generate-order-number');

/** 经营分析聚合 economic_analyses */
export const getBusinessAnalyticsSummary = (params) =>
  instance.get('/business-analytics/biz/summary', { params });
export const getBusinessAnalyticsCosts = (params) =>
  instance.get('/business-analytics/biz/costs', { params });
export const getBusinessAnalyticsRevenues = (params) =>
  instance.get('/business-analytics/biz/revenues', { params });
export const getBusinessAnalyticsIoCompare = (params) =>
  instance.get('/business-analytics/biz/io-compare', { params });
export const getBusinessAnalyticsDecisionHints = (params) =>
  instance.get('/business-analytics/biz/decision-hints', { params });
export const getBusinessAnalyticsYearlyTrend = (params) =>
  instance.get('/business-analytics/biz/yearly-trend', { params });
export const getBusinessAnalyticsAnalysisLines = (params) =>
  instance.get('/business-analytics/biz/analysis-lines', { params });
export const getBusinessAnalyticsBudgetCompare = (params) =>
  instance.get('/business-analytics/biz/budget-compare', { params });
export const getBusinessAnalyticsAnalysisLinesExport = (params) =>
  instance.get('/business-analytics/biz/analysis-lines/export', { params, responseType: 'blob' });
export const refreshBusinessEconomicAnalyses = (params) =>
  instance.post('/business-analytics/biz/refresh-economic-analyses', null, { params });
export const saveBusinessPlanActualSnapshot = (params, rows) =>
  instance.post('/business-analytics/biz/plan-actual-snapshot', rows, { params });
/** 茬口经营台账（地块 + 年度 + 茬口：收入 / 人工 / 机械 / 其他） */
export const getBusinessSeasonLedgers = (params) =>
  instance.get('/business-analytics/biz/season-ledgers', { params });
export const suggestBusinessSeasonLedger = (params) =>
  instance.get('/business-analytics/biz/season-ledgers/suggest', { params });
export const saveBusinessSeasonLedger = (params, body) =>
  instance.post('/business-analytics/biz/season-ledgers', body, { params });
export const deleteBusinessSeasonLedger = (id, params) =>
  instance.delete(`/business-analytics/biz/season-ledgers/${encodeURIComponent(id)}`, { params });
